/**
 * Svelte action: fires `onTick` on press, then repeats while held.
 * Stops automatically when the button becomes `disabled`.
 *
 * Usage:
 *   <button use:repeatOnHold={minusA} disabled={...}>−</button>
 */
export function repeatOnHold(node: HTMLButtonElement, onTick: () => void) {
    let onTickRef = onTick;
    let timeout: number | undefined;
    let interval: number | undefined;

    const INITIAL_DELAY = 400;
    const REPEAT_INTERVAL = 80;

    function tick() {
        if (node.disabled) {
            stop();
            return;
        }
        onTickRef();
    }

    function start(e: PointerEvent) {
        if (node.disabled) return;
        // Only react to primary button / single touch
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        try { node.setPointerCapture(e.pointerId); } catch {}
        tick();
        timeout = window.setTimeout(() => {
            interval = window.setInterval(tick, REPEAT_INTERVAL);
        }, INITIAL_DELAY);
    }

    function stop() {
        if (timeout !== undefined) { clearTimeout(timeout); timeout = undefined; }
        if (interval !== undefined) { clearInterval(interval); interval = undefined; }
    }

    node.addEventListener('pointerdown', start);
    node.addEventListener('pointerup', stop);
    node.addEventListener('pointerleave', stop);
    node.addEventListener('pointercancel', stop);

    return {
        update(newFn: () => void) {
            onTickRef = newFn;
        },
        destroy() {
            stop();
            node.removeEventListener('pointerdown', start);
            node.removeEventListener('pointerup', stop);
            node.removeEventListener('pointerleave', stop);
            node.removeEventListener('pointercancel', stop);
        }
    };
}
